import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import {
  getDomains, createDomain, refreshDomain, deleteDomain,
} from '@/api/tracker-domains';
import type { TrackerDomain, VerificationRecord } from '@/api/tracker-types';

function statusBadge(status: string) {
  const cls = status === 'active' ? 'bg-success-soft text-success'
    : status === 'pending' ? 'bg-warning-soft text-warning'
    : 'bg-danger-soft text-danger';
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{status}</span>;
}

function parseVerification(json: string | null): VerificationRecord[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) return parsed as VerificationRecord[];
    // Backward-compat: legacy rows stored a single object.
    if (parsed && typeof parsed === 'object') return [parsed as VerificationRecord];
    return [];
  } catch { return []; }
}

export function TrackerDomains() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [hostname, setHostname] = useState('');
  const [error, setError] = useState('');
  const [detail, setDetail] = useState<TrackerDomain | null>(null);

  const { data: domains = [], isLoading } = useQuery({
    queryKey: ['tracker-domains'],
    queryFn: getDomains,
  });

  const createMut = useMutation({
    mutationFn: (h: string) => createDomain(h),
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ['tracker-domains'] }); setModalOpen(false); setHostname(''); setError(''); setDetail(d); },
    onError: (e: Error) => setError(e.message),
  });

  const refreshMut = useMutation({
    mutationFn: refreshDomain,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-domains'] }),
  });

  const deleteMut = useMutation({
    mutationFn: deleteDomain,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-domains'] }),
    onError: (e: Error) => alert(e.message),
  });

  const columns = [
    { key: 'hostname', label: 'Hostname', render: (d: TrackerDomain) => (
      <span className="font-medium">
        {d.hostname}
        {d.app_id && <span className="ml-2 text-xs font-normal text-content-subtle">{d.app_name ?? 'app'}'s own domain</span>}
        {d.seo_domain_id && <span className="ml-2 text-xs font-normal text-content-subtle">SEO domain</span>}
      </span>
    ) },
    { key: 'status', label: 'Status', render: (d: TrackerDomain) => statusBadge(d.status) },
    { key: 'ssl_status', label: 'SSL', render: (d: TrackerDomain) => <span className="text-xs text-content-muted">{d.ssl_status || '-'}</span> },
    { key: 'last_checked_at', label: 'Last checked', render: (d: TrackerDomain) => <span className="text-xs text-content-subtle">{d.last_checked_at || '-'}</span> },
    {
      // An app's own domain is bought and attached by its build (Build tab), an
      // SEO domain on the SEO Domains page; neither is managed here.
      key: 'actions', label: '', render: (d: TrackerDomain) => d.app_id || d.seo_domain_id ? null : (
        <div className="flex gap-2">
          <button onClick={(e) => { e.stopPropagation(); refreshMut.mutate(d.id); }} className="text-xs text-primary hover:underline">Refresh</button>
          <button onClick={(e) => { e.stopPropagation(); if (confirm(`Delete ${d.hostname}?`)) deleteMut.mutate(d.id); }} className="text-xs text-danger hover:underline">Delete</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Tracker Domains</h1>
          <p className="text-sm text-content-subtle mt-1">Custom domains for tracker links (CF for SaaS)</p>
        </div>
        <EditOnly><Button onClick={() => { setHostname(''); setError(''); setModalOpen(true); }}>Add domain</Button></EditOnly>
      </div>

      <DataTable
        columns={columns}
        data={domains}
        loading={isLoading}
        keyExtractor={(d) => d.id}
        onRowClick={(d) => { if (!d.app_id && !d.seo_domain_id) setDetail(d); }}
      />

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Add tracker domain" size="md">
        <div className="space-y-4">
          {error && <div className="p-3 bg-danger-soft text-sm text-danger rounded">{error}</div>}
          <div>
            <label className="block text-sm font-medium mb-1">Hostname</label>
            <input
              type="text"
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              placeholder="landing-xyz.com"
              className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm"
            />
            <p className="mt-1 text-xs text-content-subtle">After creation you'll get a CNAME instruction to set at your DNS provider (Namecheap).</p>
          </div>
          <div className="text-xs text-content-subtle bg-surface-muted p-2 rounded">
            <strong>One-time setup check:</strong> in CF Dashboard → Zone <code>qoooql.com</code> → SSL/TLS → Custom Hostnames → Fallback Origin must be set (e.g. <code>p.qoooql.com</code>). Without it, CF will not issue SSL for custom hostnames.
          </div>
          <div className="flex justify-end gap-3 pt-3 border-t border-border">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={() => createMut.mutate(hostname.trim())} loading={createMut.isPending}>Create</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={!!detail} onClose={() => setDetail(null)} title={detail?.hostname || ''} size="lg">
        {detail && (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2">Status: {statusBadge(detail.status)} · SSL: <span className="text-xs">{detail.ssl_status || '-'}</span></div>

            <div className="bg-primary/10 border border-primary/30 p-3 rounded">
              <div className="text-xs font-semibold text-primary mb-2">Step 1 — Required: routing CNAME</div>
              <div className="text-xs text-content-muted mb-2">
                At Namecheap (or your DNS provider) → DNS → Add record:
              </div>
              <div className="font-mono text-xs bg-surface p-2 rounded border border-primary/30">
                <div><span className="text-content-subtle">type:</span> CNAME</div>
                <div><span className="text-content-subtle">host:</span> {detail.hostname.includes('.') && detail.hostname.split('.').length > 2 ? detail.hostname.split('.')[0] : '@'}</div>
                <div><span className="text-content-subtle">value:</span> p.qoooql.com</div>
              </div>
              <div className="text-xs text-content-muted mt-2">
                This is the routing record — without it traffic will not reach our worker. On an apex domain (root like <code>example.com</code>) Namecheap does not allow CNAME — use a subdomain (<code>track.example.com</code>) or ALIAS/ANAME if your provider supports it.
              </div>
            </div>

            {(() => {
              const records = parseVerification(detail.verification_record_json);
              if (records.length === 0) return null;
              return (
                <div className="bg-surface-muted border border-border p-3 rounded space-y-2">
                  <div className="text-xs font-semibold text-content">Step 2 — Required: SSL validation TXT</div>
                  <div className="text-xs text-content-muted">
                    Add {records.length === 1 ? 'this TXT record' : `all ${records.length} TXT records`} at your DNS provider. CF cross-signs with multiple CAs, so all values must be present (same host, different values is valid).
                  </div>
                  {records.map((v, i) => (
                    <div key={i} className="font-mono text-xs bg-surface p-2 rounded border border-border">
                      <div><span className="text-content-subtle">type:</span> {v.type.toUpperCase()}</div>
                      <div><span className="text-content-subtle">host:</span> {v.name}</div>
                      <div><span className="text-content-subtle">value:</span> {v.value}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            <div className="text-xs text-content-subtle">
              After setting DNS, wait 5–30 minutes and click Refresh. The cron also picks it up automatically every 30 minutes.
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t border-border">
              <Button variant="secondary" onClick={() => refreshMut.mutate(detail.id)} loading={refreshMut.isPending}>Refresh status</Button>
              <Button onClick={() => setDetail(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
